import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CheckCircle2, XCircle, Calendar, Sparkles, AlertCircle, Clock, TrendingUp, ArrowLeft, Target, X, Filter as FilterIcon, LayoutGrid } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { usePreferences } from '../../context/PreferencesContext';
import { useModuleAccess } from '../../hooks/useModuleAccess';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Duration options — special sentinel "all" fetches every plan for the grade
// and renders them grouped/sorted so users can compare across durations.
const ALL_DURATIONS = 'all';
const DURATIONS = [
  { value: ALL_DURATIONS, label: 'All Durations' },
  { value: 30, label: '1 Month' },
  { value: 90, label: '3 Months' },
  { value: 180, label: '6 Months' },
  { value: 270, label: '9 Months' },
  { value: 365, label: '1 Year' }
];

// Module display names — kept in sync with the module_access matrix on plans.
// Any module a plan can expose here will:
//   • Render as a checkmark/x-mark row inside each plan card, and
//   • Be a valid `?highlight=<key>` deep-link target for the upgrade funnel.
const MODULE_NAMES = {
  practice_sessions: 'Practice Sessions',
  progress_tracker: 'Progress Tracker',
  notebook: 'Study Notebook',
  exam_insights: 'Exam Insights',
  live_classes: 'Live Classes'
};

const PlansPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { preferences } = usePreferences();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  // Default to "All Durations" so first-time visitors see the complete catalogue.
  // Can be overridden via ?duration=<days> deep-links (from checkout redirects, etc).
  const [selectedDuration, setSelectedDuration] = useState(ALL_DURATIONS);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  // ── Cross-module upgrade funnel ──────────────────────────────────────
  // Any module card anywhere in the app can navigate here with
  // ?highlight=<module_key> to spotlight the plans that include that
  // module. Two visual modes:
  //   • highlight (default) — all plans shown; matching ones get a ring +
  //     pill, non-matching dim. Best for first-timers who want context.
  //   • filter — only matching plans shown; toggle via the chip button.
  // The chip is dismissible (removes the query param).
  const highlightModule = searchParams.get('highlight');
  const [filterMode, setFilterMode] = useState(false);
  const highlightedCardRef = useRef(null);

  // Get module statuses for filtering
  const {
    getPracticeSessionsStatus,
    getProgressTrackerStatus,
    getNotebookStatusForGrade,
    getInsightsStatusForGrade,
    getLiveClassesStatusForGrade,
    getModuleStatus
  } = useModuleAccess();

  // Get visible modules based on status (filter out hidden, mark coming soon)
  const getVisibleModules = () => {
    const userGrade = preferences?.grade;
    // Live Classes rolls out grade-by-grade. Resolve grade-specific status
    // first, then fall back to the parent `live_classes` row so plans that
    // *include* Live Classes don't get hidden on the pricing card just
    // because the current grade's launch is still pending — the checkmark
    // then acts as an aspirational preview.
    const gradeLc = userGrade && getLiveClassesStatusForGrade
      ? getLiveClassesStatusForGrade(userGrade)
      : null;
    const parentLc = getModuleStatus ? getModuleStatus('live_classes') : null;
    const moduleStatuses = {
      practice_sessions: getPracticeSessionsStatus(),
      progress_tracker: getProgressTrackerStatus(),
      notebook: userGrade ? getNotebookStatusForGrade(userGrade) : 'active',
      exam_insights: userGrade ? getInsightsStatusForGrade(userGrade) : 'active',
      live_classes: gradeLc ?? parentLc ?? 'active'
    };

    return Object.entries(MODULE_NAMES)
      .filter(([key]) => moduleStatuses[key] !== 'hidden')
      .map(([key, label]) => ({
        key,
        label,
        isComingSoon: moduleStatuses[key] === 'coming_soon'
      }));
  };

  const visibleModules = getVisibleModules();

  // Check for upgrade_to parameter from URL
  const upgradeToId = searchParams.get('upgrade_to');
  const durationParam = searchParams.get('duration');

  useEffect(() => {
    // Check if user is authenticated
    if (!isAuthenticated()) {
      toast.error('Please login to view plans');
      navigate('/');
      return;
    }

    // Check if user has set preferences
    if (!preferences.grade || !preferences.mmd_location) {
      setLoading(false);
      return;
    }

    // If duration param is set, use it (numeric); "all" is the string sentinel.
    if (durationParam) {
      const parsed = durationParam === ALL_DURATIONS ? ALL_DURATIONS : parseInt(durationParam);
      setSelectedDuration(parsed);
    }

    // Fetch subscription status
    fetchSubscriptionStatus();
  }, [preferences]);

  useEffect(() => {
    if (preferences.grade) {
      fetchPlans(selectedDuration);
    }
  }, [selectedDuration, preferences.grade]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/user/subscription`);
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    }
  };

  const fetchPlans = async (duration) => {
    try {
      setLoading(true);

      // "All Durations" → fetch every plan for this grade in one call,
      // sort by (duration asc, price asc) so users read shortest→longest
      // top-to-bottom, cheapest-first within each duration bucket.
      if (duration === ALL_DURATIONS) {
        const response = await axios.get(
          `${API}/plans/available?grade=${encodeURIComponent(preferences.grade)}`
        );
        const all = (response.data || []).slice().sort((a, b) => {
          const da = a.duration_days ?? 999999;
          const dbd = b.duration_days ?? 999999;
          if (da !== dbd) return da - dbd;
          return (a.price ?? 0) - (b.price ?? 0);
        });
        setPlans(all);
        return;
      }

      const response = await axios.get(
        `${API}/plans/by-duration?duration_days=${duration}&grade=${encodeURIComponent(preferences.grade)}`
      );
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      // Fallback to old endpoint
      try {
        const fallbackResponse = await axios.get(`${API}/plans/available?grade=${encodeURIComponent(preferences.grade)}`);
        const filteredPlans = duration === ALL_DURATIONS
          ? (fallbackResponse.data || [])
          : (fallbackResponse.data || []).filter(p => p.duration_days === duration);
        setPlans(filteredPlans);
      } catch (fallbackError) {
        toast.error('Failed to load plans');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Highlight helpers ────────────────────────────────────────────────
  // A plan is "highlighted" when the URL requested a module spotlight
  // AND the plan actually exposes that module in its module_access matrix.
  const isPlanHighlighted = (plan) => {
    if (!highlightModule) return false;
    const ma = plan.module_access || {};
    return !!ma[highlightModule];
  };

  const highlightedCount = highlightModule
    ? plans.filter(isPlanHighlighted).length
    : 0;
  const highlightModuleLabel = highlightModule && MODULE_NAMES[highlightModule]
    ? MODULE_NAMES[highlightModule]
    : highlightModule;

  // Clear the highlight — removes ?highlight=… from the URL and drops
  // filter mode back to default.
  const clearHighlight = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('highlight');
    setSearchParams(params, { replace: true });
    setFilterMode(false);
  };

  // Once plans are loaded and a highlight is active, smoothly scroll the
  // first matching card into view so the eye lands on it without hunting.
  useEffect(() => {
    if (!highlightModule || plans.length === 0) return;
    // Give the DOM a tick to render the cards + ring class before scrolling.
    const t = setTimeout(() => {
      highlightedCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 250);
    return () => clearTimeout(t);
  }, [highlightModule, plans]);

  // Plans to actually render depends on mode. Compute once per render.
  const plansToRender = highlightModule && filterMode
    ? plans.filter(isPlanHighlighted)
    : plans;

  const handlePlanSelect = (plan) => {
    // Calculate upgrade cost if user is on a paid plan
    let checkoutData = { plan };
    
    if (subscriptionStatus?.is_active && subscriptionStatus?.current_plan) {
      const currentPlan = subscriptionStatus.current_plan;
      const dailyRate = currentPlan.price / currentPlan.duration_days;
      const daysRemaining = currentPlan.days_remaining || 0;
      const rebate = dailyRate * daysRemaining;
      const upgradeCost = Math.max(0, plan.price - rebate);
      
      checkoutData = {
        ...checkoutData,
        isUpgrade: true,
        rebateAmount: rebate,
        upgradeCost: upgradeCost,
        currentPlanName: currentPlan.name
      };
    }
    
    // Navigate to checkout page with plan details
    navigate('/checkout', { state: checkoutData });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDailyRate = (price, days) => {
    const rate = price / days;
    return `${formatCurrency(rate)}/day`;
  };

  // Check if preferences are set
  if (!loading && (!preferences.grade || !preferences.mmd_location)) {
    return (
      <Layout>
        <div className="container mx-auto p-6 max-w-2xl">
          <Card>
            <CardContent className="py-12 text-center">
              <div className="mx-auto mb-6 h-16 w-16 rounded-full bg-orange-100 flex items-center justify-center">
                <AlertCircle className="h-8 w-8 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold mb-3">Complete Your Profile First</h2>
              <p className="text-gray-600 mb-6">
                Please set your preferences (Grade and MMD Location) in your profile before choosing a plan. 
                This helps us show you the most relevant plans for your preparation.
              </p>
              <Button 
                size="lg"
                onClick={() => navigate('/profile')}
              >
                Go to Profile
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto p-6">
          <div className="flex justify-center items-center h-64">
            <div className="w-8 h-8 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin"></div>
          </div>
        </div>
      </Layout>
    );
  }

  // Plans Display Screen
  return (
    <Layout>
      <div className="container mx-auto p-6 max-w-6xl">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Header */}
        <div className="text-center mb-8">
          <Badge className="mb-3 bg-teal-600">{preferences.grade}</Badge>
          <h1 className="text-3xl font-bold mb-3">Choose Your Plan</h1>
          <p className="text-gray-600">Unlock all answers and features with our premium plans</p>
          
          {/* Current Plan Info */}
          {subscriptionStatus?.is_active && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg inline-flex items-center gap-3">
              <Badge variant="outline" className="bg-white">Current Plan</Badge>
              <span className="font-medium">{subscriptionStatus.plan_name}</span>
              {subscriptionStatus.subscription_expires_at && (
                <span className="text-sm text-gray-600">
                  • Expires {new Date(subscriptionStatus.subscription_expires_at).toLocaleDateString('en-IN')}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Duration Filter */}
        <div className="flex justify-center mb-6">
          <div className="flex items-center gap-3 p-2 bg-gray-100 rounded-lg">
            <Clock className="w-5 h-5 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Duration:</span>
            <Select
              value={selectedDuration.toString()}
              onValueChange={(v) => setSelectedDuration(v === ALL_DURATIONS ? ALL_DURATIONS : parseInt(v))}
            >
              <SelectTrigger className="w-[160px] bg-white" data-testid="plans-duration-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATIONS.map(d => (
                  <SelectItem key={d.value} value={d.value.toString()}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Highlight chip — shown when arriving with ?highlight=<module> */}
        {highlightModule && (
          <div
            className="max-w-3xl mx-auto mb-6 px-4 py-3 rounded-lg border flex items-center gap-3 bg-gradient-to-r from-blue-50 to-teal-50 border-blue-200"
            data-testid="plans-highlight-chip"
          >
            <Target className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div className="flex-1 text-sm text-gray-800">
              {highlightedCount === 0 ? (
                <span>
                  <strong>No plans currently include {highlightModuleLabel}.</strong>{' '}
                  Please contact support if you were expecting to find one.
                </span>
              ) : highlightedCount === plansToRender.length && !filterMode ? (
                <span>
                  Every visible plan includes <strong>{highlightModuleLabel}</strong>{' '}
                  <span aria-hidden>🎉</span>
                </span>
              ) : (
                <span>
                  {filterMode ? 'Filtered to plans with ' : 'Showing plans that include '}
                  <strong>{highlightModuleLabel}</strong>
                  <span className="text-gray-500 ml-1">
                    ({highlightedCount} of {plans.length})
                  </span>
                </span>
              )}
            </div>
            {highlightedCount > 0 && highlightedCount < plans.length && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1 bg-white"
                onClick={() => setFilterMode((m) => !m)}
                data-testid="plans-highlight-toggle-btn"
              >
                {filterMode ? (
                  <>
                    <LayoutGrid className="w-3.5 h-3.5" /> Show all
                  </>
                ) : (
                  <>
                    <FilterIcon className="w-3.5 h-3.5" /> Only these
                  </>
                )}
              </Button>
            )}
            <button
              type="button"
              onClick={clearHighlight}
              className="p-1 rounded-md hover:bg-white/60 text-gray-500 hover:text-gray-800"
              aria-label="Clear highlight"
              data-testid="plans-highlight-clear-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Plans Grid */}
        {plansToRender.length === 0 ? (
          <Card className="max-w-md mx-auto">
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Plans Available</h3>
              <p className="text-gray-600">
                {highlightModule && filterMode ? (
                  <>No plans matching <strong>{highlightModuleLabel}</strong> in this duration. Try clearing the filter or switching to All Durations.</>
                ) : selectedDuration === ALL_DURATIONS ? (
                  <>No plans found for your grade. Please contact support.</>
                ) : (
                  <>No plans found for {DURATIONS.find(d => d.value === selectedDuration)?.label || selectedDuration + ' days'} duration. Try selecting a different duration.</>
                )}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {(() => {
              // Track the first highlighted card so we can attach the scroll ref exactly once.
              let firstHighlightAssigned = false;
              return plansToRender.map((plan) => {
                const isHighlighted = upgradeToId === plan.id;
                const moduleAccess = plan.module_access || {};
                // Count only visible modules that plan includes
                const visibleModuleCount = visibleModules.filter(m => !m.isComingSoon && moduleAccess[m.key]).length;
                const totalVisibleModules = visibleModules.filter(m => !m.isComingSoon).length;

                // Cross-module highlight state (from ?highlight=<key>).
                // We render a ring + pill on matches, and dim non-matches
                // in highlight mode so the eye jumps to the right plans.
                const matchesHighlight = highlightModule ? !!moduleAccess[highlightModule] : false;
                const shouldDim = !!highlightModule && !filterMode && !matchesHighlight;
                const attachRef = matchesHighlight && !firstHighlightAssigned;
                if (attachRef) firstHighlightAssigned = true;

                return (
                <Card
                  key={plan.id}
                  ref={attachRef ? highlightedCardRef : undefined}
                  data-testid={matchesHighlight ? `plan-card-highlighted-${plan.id}` : `plan-card-${plan.id}`}
                  className={[
                    'relative transition-all',
                    isHighlighted ? 'ring-2 ring-blue-500 shadow-lg' : '',
                    plan.is_best_value ? 'border-yellow-400 border-2' : '',
                    matchesHighlight ? 'ring-2 ring-teal-500 shadow-lg' : '',
                    shouldDim ? 'opacity-60' : '',
                  ].filter(Boolean).join(' ')}
                >
                  {plan.is_best_value && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-yellow-500 text-white shadow-md">Best Value</Badge>
                    </div>
                  )}
                  {matchesHighlight && (
                    <div className="absolute -top-3 right-4">
                      <Badge className="bg-teal-600 text-white shadow-md gap-1">
                        <Target className="w-3 h-3" />
                        Includes {highlightModuleLabel}
                      </Badge>
                    </div>
                  )}
                  
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center justify-between">
                      <span>{plan.name}</span>
                      <Badge variant="outline" className="text-xs">
                        {visibleModuleCount}/{totalVisibleModules} modules
                      </Badge>
                    </CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                  </CardHeader>

                  <CardContent>
                    {/* Price */}
                    <div className="mb-4">
                      <div className="flex items-baseline gap-2">
                        <p className="text-3xl font-bold">{formatCurrency(plan.price)}</p>
                        <span className="text-sm text-gray-500">
                          {formatDailyRate(plan.price, plan.duration_days)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                        <Calendar className="w-4 h-4" />
                        {plan.duration_days} days validity
                      </p>
                      {plan.grade && plan.grade !== 'all' && (
                        <Badge variant="outline" className="mt-2 text-xs">
                          {plan.grade}
                        </Badge>
                      )}
                    </div>

                    {/* Module Access - Filtered by visibility status */}
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      {/* Available Now Section */}
                      {visibleModules.some(m => !m.isComingSoon) && (
                        <div className="mb-3">
                          <p className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            Available Now
                          </p>
                          <div className="space-y-1 pl-1">
                            {visibleModules.filter(m => !m.isComingSoon).map(({ key, label }) => {
                              const rowMatchesHighlight = highlightModule === key && moduleAccess[key];
                              return (
                                <div
                                  key={key}
                                  className={`flex items-center gap-2 text-sm ${rowMatchesHighlight ? 'font-semibold' : ''}`}
                                >
                                  {moduleAccess[key] ? (
                                    <CheckCircle2 className={`w-4 h-4 flex-shrink-0 ${rowMatchesHighlight ? 'text-teal-600' : 'text-green-600'}`} />
                                  ) : (
                                    <XCircle className="w-4 h-4 text-gray-300 flex-shrink-0" />
                                  )}
                                  <span className={moduleAccess[key] ? (rowMatchesHighlight ? 'text-teal-700' : 'text-gray-900') : 'text-gray-400'}>
                                    {label}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Coming Soon Section */}
                      {visibleModules.some(m => m.isComingSoon) && (
                        <div className={visibleModules.some(m => !m.isComingSoon) ? 'pt-2 border-t border-gray-200' : ''}>
                          <p className="text-xs font-semibold text-amber-600 mb-2 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Coming Soon
                          </p>
                          <div className="space-y-1 pl-1">
                            {visibleModules.filter(m => m.isComingSoon).map(({ key, label }) => (
                              <div key={key} className="flex items-center gap-2 text-sm">
                                <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
                                <span className="text-amber-700">{label}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Features */}
                    {plan.features && plan.features.length > 0 && (
                      <div className="space-y-1 mb-4">
                        {plan.features.slice(0, 3).map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-700">{feature}</span>
                          </div>
                        ))}
                        {plan.features.length > 3 && (
                          <p className="text-xs text-gray-500 ml-6">
                            +{plan.features.length - 3} more features
                          </p>
                        )}
                      </div>
                    )}

                    {/* Select Button */}
                    <Button
                      className={`w-full ${isHighlighted ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                      onClick={() => handlePlanSelect(plan)}
                      disabled={processingPayment}
                    >
                      {processingPayment ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Processing...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          {subscriptionStatus?.is_active ? 'Upgrade to This' : 'Choose Plan'}
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
              });
            })()}
          </div>
        )}

        {/* Pro-rata Info */}
        {subscriptionStatus?.is_active && (
          <div className="max-w-2xl mx-auto p-4 bg-blue-50 rounded-lg border border-blue-200 text-center">
            <TrendingUp className="w-6 h-6 text-blue-600 mx-auto mb-2" />
            <h4 className="font-semibold text-blue-900 mb-1">Pro-rata Pricing</h4>
            <p className="text-sm text-blue-800">
              Upgrading? Your unused days from the current plan will be credited towards the new plan. 
              You only pay the difference!
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PlansPage;
